import './App.css';

import { CobaltThemeProvider } from '@cae/cobalt-react/theme';
import {
  getSnapshot,
  subscribe,
} from '@configs/axios/forbidden-store/forbiddenStore';
import { useHeapUserProperties } from '@configs/heap';
import { queryClient } from '@configs/queryClient';
import {
  useAuth,
  AuthProvider,
  useReauthenticate,
} from '@contexts/auth-provider';
import { useLDIdentify } from '@contexts/launch-darkly-provider/hooks/use-ld-identify/useLDIdentify';
import { ModulesProvider } from '@contexts/modules-provider';
import { useMsalAuth } from '@contexts/msal-provider';
import { OrganizationsProvider } from '@contexts/organizations-provider';
import { PermissionsProvider } from '@contexts/permissions-provider';
import { MaintenancePage } from '@pages/maintenance/MaintenancePage';
import { parseAppMaintenancePageFlag } from '@pages/maintenance/MaintenancePage.utils';
import { router } from '@routes/router';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { ErrorBoundary } from '@shared/components/error-boundary/ErrorBoundary';
import { AuthErrorScreen } from '@shared/components/error-pages/auth-error/AuthErrorScreen';
import { ErrorPage } from '@shared/components/error-pages/ErrorPage';
import { SessionExpiredError } from '@shared/components/error-pages/session-expired/SessionExpiredError';
import { getCurrentDeepLinkUrl } from '@shared/helpers/deep-link/getCurrentDeepLinkUrl';
import { getCurrentAuthType } from '@shared/helpers/msal-instance/msalInstance';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { RouterProvider } from 'react-router-dom';

const appRouter = router();

/**
 * Always render ModulesProvider and OrganizationsProvider for deep linking support.
 * - Both providers handle unauthenticated state gracefully (queries gated by isAuthenticated)
 * - OrganizationsProvider reads ModulesContext internally to compute isManageModule
 * - PermissionsProvider only renders when authenticated to avoid API calls without a session
 * - This allows OrgRouteGuard to work for deep links
 */
function AppContent(): React.ReactElement {
  const { status, isAuthenticated, retry } = useAuth();
  const { login } = useMsalAuth();
  const { reauthenticate, hasFailed: hasReauthFailed } = useReauthenticate();
  const handleLogin = React.useCallback((): void => {
    void login(getCurrentAuthType(), getCurrentDeepLinkUrl()).catch(error => {
      console.error('MSAL login failed:', error);
    });
  }, [login]);
  const isForbidden = React.useSyncExternalStore(subscribe, getSnapshot);
  const { appMaintenancePage } = useFlags();
  const maintenancePage = parseAppMaintenancePageFlag(appMaintenancePage);

  if (status === 'sessionExpired') {
    return hasReauthFailed ? (
      <AuthErrorScreen onRetry={reauthenticate} />
    ) : (
      <SessionExpiredError onLogin={reauthenticate} />
    );
  }

  if (status === 'error') {
    return <AuthErrorScreen onRetry={retry} onLogin={handleLogin} />;
  }

  if (status === 'initializing') {
    return <AppLoader />;
  }

  if (isAuthenticated && maintenancePage.enabled) {
    return <MaintenancePage flag={maintenancePage} />;
  }

  if (isForbidden) {
    return <ErrorPage type="forbidden" />;
  }

  return (
    <ModulesProvider>
      <OrganizationsProvider>
        <LDIdentifyBridge />
        <HeapIdentifyBridge />
        {isAuthenticated ? (
          <PermissionsProvider>
            <RouterProvider router={appRouter} />
          </PermissionsProvider>
        ) : (
          <RouterProvider router={appRouter} />
        )}
      </OrganizationsProvider>
    </ModulesProvider>
  );
}

/**
 * Bridge component that calls useLDIdentify inside the
 * ModulesProvider + OrganizationsProvider tree where the hook's
 * dependencies (useModules, useOrganizations) are available.
 */
function LDIdentifyBridge(): null {
  useLDIdentify();
  return null;
}

/**
 * Bridge component that calls useHeapUserProperties inside the
 * ModulesProvider + OrganizationsProvider tree where the hook's
 * dependencies (useModules, useOrganizations) are available.
 */
const HeapIdentifyBridge = React.memo(function HeapIdentifyBridge(): null {
  useHeapUserProperties();
  return null;
});

HeapIdentifyBridge.displayName = 'HeapIdentifyBridge';

function App(): React.ReactElement {
  return (
    <CobaltThemeProvider initialTheme="light" initialBrand="default">
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AuthProvider>
            <AppContent />
            <ReactQueryDevtools buttonPosition="bottom-right" />
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </CobaltThemeProvider>
  );
}

export { App };
